import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tag } from "primereact/tag";
import { Rating } from "primereact/rating";
import { useDispatch, useSelector } from "react-redux";
import { getSpecificReview } from "../../store/AsyncMethods/ReviewsMethod";

export default function ViewReview() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { reviewId } = useParams();
  const { specificReview, loading } = useSelector((state) => state.ReviewReducers);

  useEffect(() => {
    if (reviewId) {
      dispatch(getSpecificReview(reviewId));
    }
  }, [reviewId, dispatch]);

  const getStatusSeverity = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "danger";
      default:
        return null;
    }
  };

  const handleEdit = () => {
    navigate(`/edit-review/${reviewId}`);
  };

  const handleBack = () => {
    navigate("/manage-reviews");
  };

  if (loading) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading review data...</p>
        </div>
      </div>
    );
  }

  if (!specificReview) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Review not found</p>
          <Button
            label="Back to Reviews"
            icon="pi pi-arrow-left"
            className="p-button-secondary mt-4"
            onClick={handleBack}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-sm text-gray-500">Home &gt; Manage Reviews &gt; View Review</p>
        </div>
      </div>

      <Card className="max-w-3xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Review Details</h2>
              <p className="text-gray-600">Review ID: #{specificReview.id}</p>
            </div>
            <Tag
              value={specificReview.status}
              severity={getStatusSeverity(specificReview.status)}
              className="capitalize"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Information
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold text-gray-800">
                  {specificReview.customer_name || specificReview.customer}
                </p>
                <p className="text-sm text-gray-600">
                  {specificReview.customer_email || "No email provided"}
                </p>
                {specificReview.verified && (
                  <Tag value="Verified Customer" severity="success" className="mt-2" />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold text-gray-800">
                  {specificReview.product_name || specificReview.product}
                </p>
                <p className="text-sm text-gray-600">
                  Order: {specificReview.order_id || "N/A"}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <Rating 
                    value={specificReview.rating} 
                    readOnly 
                    stars={5} 
                    cancel={false} 
                  />
                  <span className="text-sm text-gray-600">
                    ({specificReview.rating}/5 stars)
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Date
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-800">
                  {specificReview.created_at 
                    ? new Date(specificReview.created_at).toLocaleDateString()
                    : "N/A"
                  }
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Helpful Votes
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-800">
                  {specificReview.helpful_votes || specificReview.helpful || 0} people found this helpful
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="bg-gray-50 p-3 rounded-lg">
                <Tag
                  value={specificReview.status}
                  severity={getStatusSeverity(specificReview.status)}
                  className="capitalize"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Comment
          </label>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-800 leading-relaxed">
              {specificReview.comment || specificReview.review_text}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            label="Edit Review"
            icon="pi pi-pencil"
            className="p-primary-btn"
            onClick={handleEdit}
          />
          <Button
            label="Back to Reviews"
            icon="pi pi-arrow-left"
            className="p-button-secondary"
            onClick={handleBack}
          />
        </div>
      </Card>
    </div>
  );
} 